import React, { useState, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import MobileBottomNav from "./MobileBottomNav";
import WelcomeSplash from "../WelcomeSplash";
import GuidedTour from "../GuidedTour";
import GuestSignupNudge from "../GuestSignupNudge";
import ConsentBanner from "../ConsentBanner";
import { Sheet, SheetContent } from "../ui/sheet";
import { useApp } from "../../context/AppContext";
import { track, startSession } from "../../lib/analytics";
import { WifiOff } from "lucide-react";

const SCREEN_NAME = (p) => (p === "/" ? "/" : p);

export default function AppShell() {
  const [open, setOpen] = useState(false);
  const { online, t, tourOpen, setTourOpen } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const hideNav = ["/check-in", "/crop-check", "/plant"].includes(location.pathname);

  // session + screen_viewed with dwell_seconds
  useEffect(() => { startSession(); }, []);
  useEffect(() => {
    const screen = SCREEN_NAME(location.pathname);
    const enteredAt = Date.now();
    track("screen_viewed", { screen, entry_source: "nav" });
    return () => {
      const dwell = Math.round((Date.now() - enteredAt) / 1000);
      track("screen_viewed", { screen, dwell_seconds: dwell, exit_action: "navigate" });
    };
  }, [location.pathname]);

  // Launch tour from ?tour=1 (e.g. after demo login from the landing page)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("tour") === "1") {
      setTourOpen(true);
      navigate("/", { replace: true });
    }
  }, [location.search]);

  return (
    <div className="min-h-screen flex" style={{ background: "linear-gradient(160deg, #fbf8ef 0%, #f8f6ee 45%, #f2f5ec 100%)" }}>
      <WelcomeSplash variant="post" />
      <GuestSignupNudge />
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-[264px] shrink-0 flex-col fixed inset-y-0 z-30">
        <Sidebar />
      </aside>

      {/* Mobile drawer */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="p-0 w-[290px] z-[10001] border-0 rounded-r-[46px] overflow-hidden shadow-2xl">
          <Sidebar onNavigate={() => setOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="flex-1 lg:ml-[264px] min-w-0 flex flex-col overflow-x-hidden">
        <Topbar onMenu={() => setOpen(true)} />
        {!online && (
          <div className="bg-warning/15 text-warning text-sm px-4 py-2 flex items-center justify-center gap-2 font-medium" data-testid="offline-banner">
            <WifiOff className="h-4 w-4" /> {t("offlineMode")}
          </div>
        )}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 pb-24 lg:pb-8 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
      {!hideNav && <MobileBottomNav onMore={() => setOpen(true)} />}
      {!hideNav && <ConsentBanner />}
      {tourOpen && <GuidedTour onClose={() => setTourOpen(false)} />}
    </div>
  );
}
